import { isInCheck } from './attacks.js';
import { isInsufficientMaterial } from './material.js';
import { opposite, type Color, type Position } from './types.js';
import type { RuleSet } from './rules/types.js';

const TERMINATIONS = [
  'checkmate', 'stalemate', 'insufficient', 'threefold', 'fifty-move', 'resign', 'agreement', 'flag',
] as const;

export type Termination = (typeof TERMINATIONS)[number];

/** Guard for a termination read back from storage or the wire. */
export function isTermination(value: unknown): value is Termination {
  return typeof value === 'string' && (TERMINATIONS as readonly string[]).includes(value);
}

export interface Outcome {
  /** Null for a draw. */
  readonly winner: Color | null;
  readonly termination: Termination;
}

/**
 * The outcome the position itself forces, or null while play continues.
 * Checkmate and stalemate outrank the draw rules: a mating move that also
 * completes a repetition is still mate.
 */
export function classifyPosition(
  pos: Position,
  legalMoveCount: number,
  repetitions: number,
  rules: RuleSet,
): Outcome | null {
  if (legalMoveCount === 0) {
    return isInCheck(pos.board, pos.turn)
      ? { winner: opposite(pos.turn), termination: 'checkmate' }
      : { winner: null, termination: 'stalemate' };
  }
  if (isInsufficientMaterial(pos.board)) return { winner: null, termination: 'insufficient' };
  if (repetitions >= rules.repetitionLimit) return { winner: null, termination: 'threefold' };
  if (pos.halfmoveClock >= rules.fiftyMovePlies) return { winner: null, termination: 'fifty-move' };
  return null;
}
